export type TimezoneMode = "utc" | "local";

export function createDateFormatter(
  timezone: TimezoneMode,
  options: Intl.DateTimeFormatOptions,
  locales: string | string[] = "en-GB",
): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(locales, {
    ...options,
    timeZone: timezone === "utc" ? "UTC" : undefined,
  });
}

export function displayDate(value: Date, timezone: TimezoneMode): Date {
  return timezone === "utc"
    ? value
    : new Date(
        Date.UTC(
          value.getFullYear(),
          value.getMonth(),
          value.getDate(),
          value.getHours(),
          value.getMinutes(),
          value.getSeconds(),
          value.getMilliseconds(),
        ),
      );
}

export function dateFromDisplayParts(
  day: string,
  time: string,
  timezone: TimezoneMode,
  reference?: string,
): Date {
  const prefix = `${day}T${time}`;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || !/^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(time))
    return new Date(NaN);
  if (reference) {
    const original = new Date(reference);
    if (
      Number.isFinite(original.getTime()) &&
      displayDate(original, timezone).toISOString().startsWith(prefix)
    )
      return original;
  }
  const date = new Date(`${prefix}${timezone === "utc" ? "Z" : ""}`);
  return Number.isFinite(date.getTime()) &&
    displayDate(date, timezone).toISOString().startsWith(prefix)
    ? date
    : new Date(NaN);
}

export function formatDisplayDay(value: number | Date, timezone: TimezoneMode): string {
  const parts = Object.fromEntries(
    createDateFormatter(timezone, { weekday: "short", day: "numeric", month: "short" }, "en")
      .formatToParts(value)
      .map((part) => [part.type, part.value]),
  );
  return `${parts.weekday} ${parts.day} ${parts.month}`;
}

const pad = (value: number) => String(value).padStart(2, "0");

export function formatDateTime(value: Date | string, timezone: TimezoneMode): string {
  const date = displayDate(value instanceof Date ? value : new Date(value), timezone);
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
}

export function formatTime(value: Date | string, timezone: TimezoneMode): string {
  const date = displayDate(value instanceof Date ? value : new Date(value), timezone);
  return `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
}

export function formatHour(value: Date, timezone: TimezoneMode): string {
  return `${pad(displayDate(value, timezone).getUTCHours())}:00`;
}

export function formatDateRange(start: Date, end: Date, timezone: TimezoneMode): string {
  return `${formatDateTime(start, timezone)} \u2192 ${formatDateTime(end, timezone)}`;
}

const options: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
};
const formatters = {
  utc: createDateFormatter("utc", options, "en-US"),
  local: createDateFormatter("local", options, "en-US"),
};

export function formatAdminDateTime(
  value: Date | string | null | undefined,
  timezone: TimezoneMode,
): string {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  const parts = Object.fromEntries(
    formatters[timezone].formatToParts(date).map(({ type, value: part }) => [type, part]),
  );
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
}
